import React, { useEffect, useState } from "react";
import { ChevronDown, Trophy } from "lucide-react";
import leaderboardService from "../../services/leaderboardService";
import { getAvatarStyle } from "../../utils/avatarColors";
import { useFormat, useTranslation } from "../../i18n";
import en from "../../locales/en/widgets.json";
import WidgetShell from "./WidgetShell";

/**
 * English copy for the exported helpers when no `t` is supplied — tests, and
 * callers outside a component. It reads the same catalog the runtime bundles,
 * so the fallback cannot drift from what the widget renders.
 */
const englishT = (key, vars = {}) => {
  const found = key
    .split(".")
    .reduce((node, part) => (node == null ? undefined : node[part]), en);
  return String(found === undefined ? key : found).replace(
    /\{\{\s*(\w+)\s*\}\}/g,
    (match, name) =>
      vars[name] === undefined || vars[name] === null ? match : String(vars[name])
  );
};

// Direction and metric collapse into one control: a streak has no direction, so
// a separate direction filter would have to disable itself for one of three
// options, which always reads as a bug the first time you see it.
//
// `label` is the English default; the component re-labels through `t`.
export const BOARDS = [
  { key: "received", labelKey: "leaderboard.boards.received" },
  { key: "given", labelKey: "leaderboard.boards.given" },
  { key: "streaks", labelKey: "leaderboard.boards.streaks" },
].map((board) => ({ ...board, label: englishT(board.labelKey) }));

const STORAGE_KEY = "leaderboard.board";
const PERIOD_KEY = "leaderboard.period";
const SCOPE_KEY = "leaderboard.scope";

// Calendar-aligned, resolved in the org's timezone — the same boundaries the
// streak uses, so "this week" means one thing on the whole rail.
//
// "Today" rather than "Day" because these are windows you are *inside*, not
// durations: the board is showing what happened today, not a generic day.
export const PERIODS = [
  { key: "day", labelKey: "leaderboard.periods.day" },
  { key: "week", labelKey: "leaderboard.periods.week" },
  { key: "month", labelKey: "leaderboard.periods.month" },
  { key: "year", labelKey: "leaderboard.periods.year" },
].map((period) => ({ ...period, label: englishT(period.labelKey) }));

// The scope select's fixed first option. Everything after it is one of the
// viewer's own teams, named, so someone in Engineering *and* a guild picks the
// board they meant instead of getting a union of both.
export const GLOBAL_SCOPE = "org";

/**
 * Scope options for a viewer, given the teams the server says they are in.
 *
 * A "Team" filter that silently unions every team you belong to answers a
 * question nobody asked — you are not competing against the union, and no row
 * in it is a peer group you'd name. Listing the teams individually is the
 * difference between a filter and a guess.
 */
export function scopeOptions(teams = [], t = englishT) {
  return [
    { value: GLOBAL_SCOPE, label: t("leaderboard.scope.everyone") },
    ...teams.map((team) => ({ value: team.id, label: team.name })),
  ];
}

/**
 * Split a stored scope value back into the two params the API takes.
 *
 * The bare string "team" is what an org's default scope setting can say before
 * any team has been named; the server answers it with the union, which is the
 * only honest reading of "their team" for someone in three of them.
 */
export function scopeParams(value) {
  if (!value || value === GLOBAL_SCOPE) return { scope: "org", teamId: null };
  if (value === "team") return { scope: "team", teamId: null };
  return { scope: "team", teamId: value };
}

// Top three get a medal treatment. Beyond that the rank number carries it —
// colouring everything would flatten the distinction it's there to make.
const MEDALS = {
  1: { ring: "ring-amber-400", chip: "bg-amber-400 text-amber-950" },
  2: { ring: "ring-slate-300", chip: "bg-slate-300 text-slate-800" },
  3: { ring: "ring-orange-300", chip: "bg-orange-300 text-orange-950" },
};

export function initials(name, email) {
  const source = name || email || "";
  const parts = source.split(" ").filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase() || "??";
}

/**
 * The widget's heading.
 *
 * Names the population and the thing being ranked — "Engineering Recognition
 * Leaderboard" — so a glance at the rail answers "whose board is this?" without
 * cross-referencing which filter chip is lit. The population name comes from
 * the server, since team membership is not something the client holds.
 */
export function boardTitle(board, scopeLabel, t = englishT) {
  if (board === "streaks") {
    if (!scopeLabel) return t("leaderboard.title.streak");
    return t("leaderboard.title.scoped_streak", { scope: scopeLabel });
  }
  if (!scopeLabel) return t("leaderboard.title.recognition");
  return t("leaderboard.title.scoped_recognition", { scope: scopeLabel });
}

export function formatValue(board, metric, value, t = englishT) {
  if (board === "streaks") return t("leaderboard.value.weeks", { value });
  if (metric === "points") return t("leaderboard.value.points", { value });
  return String(value);
}

export function LeaderboardRow({ row, board, metric, highlight, max = 0 }) {
  const { t } = useTranslation("widgets");
  const fmt = useFormat();
  const name = row.full_name || row.email;
  const medal = MEDALS[row.rank];
  // Relative magnitude, so the gap between first and fifth is visible rather
  // than something you work out by reading numbers.
  const fill = max > 0 ? Math.max(3, (row.value / max) * 100) : 0;

  return (
    <div
      className={`relative flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg overflow-hidden ${
        highlight ? "bg-purple-50" : ""
      }`}
    >
      {fill > 0 && (
        <span
          aria-hidden="true"
          className={`absolute inset-y-0 left-0 rounded-xl transition-[width] duration-500 ${
            highlight ? "bg-purple-100" : "bg-gray-100/70"
          }`}
          style={{ width: `${fill}%` }}
        />
      )}

      <span
        className={`relative w-5 h-5 flex items-center justify-center rounded-full text-[11px] font-bold tabular-nums flex-shrink-0 ${
          medal ? medal.chip : "text-gray-500"
        }`}
      >
        {fmt.number(row.rank)}
      </span>

      {row.avatar_url ? (
        <img
          src={row.avatar_url}
          alt=""
          className={`relative w-7 h-7 rounded-full object-cover flex-shrink-0 ${
            medal ? `ring-2 ${medal.ring}` : ""
          }`}
        />
      ) : (
        <span
          className={`relative w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0 ${
            medal ? `ring-2 ${medal.ring}` : ""
          }`}
          style={getAvatarStyle(name)}
        >
          {initials(row.full_name, row.email)}
        </span>
      )}

      <span className="relative flex-1 min-w-0 truncate text-sm text-gray-800">
        {highlight ? t("leaderboard.you") : name}
      </span>
      <span className="relative text-sm font-semibold text-gray-900 tabular-nums flex-shrink-0">
        {formatValue(board, metric, fmt.number(row.value), t)}
      </span>
    </div>
  );
}

/**
 * One of the widget's three filters.
 *
 * Native `<select>` rather than the pill rows this replaced: nine controls laid
 * out flat filled the widget with chrome and left the rows — the actual content
 * — a third of the space. A closed select shows only the current value, which
 * is the one thing a filter needs to show when you are not using it.
 */
function FilterSelect({ label, value, onChange, options }) {
  return (
    <span className="relative flex-1 min-w-0">
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full appearance-none cursor-pointer truncate rounded-lg border border-gray-200 bg-white py-1 pl-2 pr-6 text-[11px] font-medium text-gray-700 hover:border-gray-300 focus:border-purple-400 focus:outline-none focus:ring-1 focus:ring-purple-400"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <ChevronDown
        aria-hidden="true"
        className="pointer-events-none absolute right-1.5 top-1/2 h-3 w-3 -translate-y-1/2 text-gray-400"
      />
    </span>
  );
}

export default function LeaderboardWidget({ config = {}, compact = false }) {
  const { t } = useTranslation("widgets");
  const [board, setBoard] = useState(() => {
    if (typeof window === "undefined") return config.default_board || "received";
    return (
      window.localStorage.getItem(STORAGE_KEY) ||
      config.default_board ||
      "received"
    );
  });
  const [period, setPeriod] = useState(() => {
    if (typeof window === "undefined") return config.default_period || "week";
    return (
      window.localStorage.getItem(PERIOD_KEY) ||
      config.default_period ||
      "week"
    );
  });
  // Either "org" or a team id. The org default can only express "team" in the
  // abstract, so it seeds the *kind* of scope and the server picks a team until
  // the viewer names one.
  const [scope, setScope] = useState(() => {
    const fallback = config.scope === "team" ? "team" : GLOBAL_SCOPE;
    if (typeof window === "undefined") return fallback;
    return window.localStorage.getItem(SCOPE_KEY) || fallback;
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const { scope: scopeKind, teamId } = scopeParams(scope);
    leaderboardService
      .getLeaderboard({ board, period, scope: scopeKind, teamId })
      .then((result) => {
        if (!cancelled) setData(result);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // metric lives in the org config and is applied server-side, so it never
    // appears in the request. It still belongs in the deps: without it an
    // admin's change leaves a mounted widget showing a board built under the
    // old setting, which reads as "the leaderboard stopped updating".
  }, [board, period, scope, config.metric]);

  // View preferences, not settings — kept local rather than synced, since
  // per-user server state is deliberately out of scope for the rail.
  const persist = (storageKey, value) => {
    try {
      window.localStorage.setItem(storageKey, value);
    } catch {
      /* private browsing — the org default is fine */
    }
  };

  const chooseBoard = (key) => {
    setBoard(key);
    persist(STORAGE_KEY, key);
  };

  const choosePeriod = (key) => {
    setPeriod(key);
    persist(PERIOD_KEY, key);
  };

  const chooseScope = (key) => {
    setScope(key);
    persist(SCOPE_KEY, key);
  };

  const teams = data?.teams || [];
  const options = scopeOptions(teams, t);

  // Resolve a scope the select cannot show onto one it can: the abstract "team"
  // an org default sets, or a team the viewer has since left. Without this the
  // control renders its first option while the state says something else, and
  // the board disagrees with the filter that is supposedly driving it.
  useEffect(() => {
    if (!data) return;
    if (options.some((option) => option.value === scope)) return;
    chooseScope(teams.length ? teams[0].id : GLOBAL_SCOPE);
    // chooseScope and the derived lists are stable for a given data/scope pair.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, scope]);

  if (data && data.enabled === false) return null;

  const rows = data?.rows || [];
  const metric = data?.metric || config.metric || "count";
  const max = rows.length ? Math.max(...rows.map((r) => r.value)) : 0;

  return (
    <WidgetShell
      widgetKey="leaderboard"
      icon={Trophy}
      title={boardTitle(board, data?.scope_label, t)}
      accentClass="text-amber-500"
      compact={compact}
      defaultCollapsed={compact}
      scroll={false}
    >
      {/* Scope gets its own row: it is the only filter whose options are
          free text, and a team name sharing a 288px row with two other selects
          truncates to nothing. Board and period are fixed short words. */}
      <div className="px-3 pt-2 space-y-1.5">
        <div className="flex">
          <FilterSelect
            label={t("leaderboard.filters.scope")}
            value={options.some((o) => o.value === scope) ? scope : GLOBAL_SCOPE}
            onChange={chooseScope}
            options={options}
          />
        </div>

        <div className="flex items-center gap-1.5">
          <FilterSelect
            label={t("leaderboard.filters.ranking")}
            value={board}
            onChange={chooseBoard}
            options={BOARDS.map((b) => ({ value: b.key, label: t(b.labelKey) }))}
          />
          <FilterSelect
            label={t("leaderboard.filters.period")}
            value={period}
            onChange={choosePeriod}
            options={PERIODS.map((p) => ({ value: p.key, label: t(p.labelKey) }))}
          />
        </div>
      </div>

      {/* Rows scroll inside a fixed budget so the toggles above and the
          viewer row below stay visible without the widget growing. */}
      <div className="p-2 overflow-y-auto" style={{ maxHeight: 132 }}>
        {loading ? (
          <div className="py-6 flex justify-center">
            <div className="animate-spin w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full" />
          </div>
        ) : rows.length === 0 ? (
          <p className="text-xs text-gray-500 text-center py-6 px-3">
            {data?.scope_empty
              ? t("leaderboard.empty.no_team")
              : board === "streaks"
                ? t("leaderboard.empty.no_streaks")
                : t("leaderboard.empty.no_activity")}
          </p>
        ) : (
          rows.map((row) => (
            <LeaderboardRow
              key={row.user_id}
              row={row}
              board={board}
              metric={metric}
              highlight={row.is_viewer}
              max={max}
            />
          ))
        )}
      </div>

      {/* Always show the viewer their own standing. Without it a leaderboard is
          a feature for the top five and a mild demotivator for everyone else. */}
      {data?.viewer_row && (
        <div className="border-t border-gray-100 bg-gray-50 p-2">
          <LeaderboardRow
            row={data.viewer_row}
            board={board}
            metric={metric}
            highlight
            max={max}
          />
        </div>
      )}

      {data?.viewer_opted_out && (
        <p className="px-4 py-2 text-[11px] text-gray-500 border-t border-gray-100">
          {t("leaderboard.opted_out")}
        </p>
      )}
    </WidgetShell>
  );
}
